import { useState, useEffect, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  StatsGrid,
  StreakAnalysisChart,
  StatisticalInsight,
  type MachineStats,
  type GapData,
  type StatisticalData,
} from "../components/dashboard";
import { useAppContext } from "../context";
import { useRealtimeEvents } from "../hooks";
import { api } from "../api";
import { Card, CardContent } from "../components/ui/card";
import { AlertTriangle, Info, LayoutGrid, LayoutList } from "lucide-react";
import { trackEvent } from "../lib/analytics";
import { cn } from "../lib/utils";

// LocalStorage keys
const TIER_SELECTION_KEY = "gap-tracker-tier-selections";
const VIEW_MODE_KEY = "gap-tracker-view-mode";

type ViewMode = "single" | "all";

interface TierInfo {
  id: number;
  name: string;
  color: string;
  minValue: number;
  maxValue: number | null;
  probability: number | null;
  displayOrder: number;
  stats: {
    totalWins: number;
    currentGap: number;
    averageGap: number | null;
    minGap: number | null;
    maxGap: number | null;
    lastWinAt: string | null;
  } | null;
}

interface MachineResponse {
  machine: {
    id: number;
    code: string;
    name: string;
    priceUsd: number;
    status: string;
    tiersConfigured: boolean;
  };
  stats: {
    totalSpins: number;
    totalTierWins: number;
    lastUpdatedAt: string | null;
  };
  tiers: TierInfo[];
  activeUsers: number;
}

interface GapResponse {
  gaps: Array<{
    id: number;
    gap: number;
    pullNumber: number | null;
    timestamp: Date;
    prizeName: string;
    prizeImage: string | null;
    insuredValue: number;
    tierId: number | null;
    tierName: string | null;
    tierColor: string | null;
  }>;
  currentGap: number;
  averageGap: number;
  minGap: number | null;
  maxGap: number | null;
  tierId: number | null;
}

interface AllTiersGapResponse {
  gaps: Array<{
    id: number;
    gap: number;
    pullNumber: number | null;
    timestamp: Date;
    prizeName: string;
    prizeImage: string | null;
    insuredValue: number;
    tierId: number | null;
    tierName: string | null;
    tierColor: string | null;
  }>;
  tiers: Array<{
    tierId: number;
    tierName: string;
    tierColor: string;
    currentGap: number;
    averageGap: number;
    minGap: number | null;
    maxGap: number | null;
    totalWins: number;
  }>;
  minGap: number | null;
  maxGap: number | null;
}

export function OverviewPage() {
  const { selectedMachine, timeRange } = useAppContext();
  const [selectedTierId, setSelectedTierId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(VIEW_MODE_KEY);
      return (stored === "all" ? "all" : "single") as ViewMode;
    }
    return "single";
  });

  // Persist view mode
  useEffect(() => {
    localStorage.setItem(VIEW_MODE_KEY, viewMode);
  }, [viewMode]);

  // Subscribe to real-time events (auto-invalidates queries on new events)
  useRealtimeEvents({
    machineCode: selectedMachine,
    enabled: !!selectedMachine,
  });

  // Load saved tier selection from localStorage
  const getSavedTierId = useCallback((machine: string): number | null => {
    try {
      const saved = localStorage.getItem(TIER_SELECTION_KEY);
      if (saved) {
        const selections = JSON.parse(saved) as Record<string, number>;
        return selections[machine] ?? null;
      }
    } catch {
      // Ignore parse errors
    }
    return null;
  }, []);

  // Save tier selection to localStorage
  const saveTierId = useCallback((machine: string, tierId: number) => {
    try {
      const saved = localStorage.getItem(TIER_SELECTION_KEY);
      const selections = saved
        ? (JSON.parse(saved) as Record<string, number>)
        : {};
      selections[machine] = tierId;
      localStorage.setItem(TIER_SELECTION_KEY, JSON.stringify(selections));
    } catch {
      // Ignore storage errors
    }
  }, []);

  // Fetch machine stats (filtered by time range)
  const {
    data: machineData,
    isLoading: isLoadingMachine,
    error: machineError,
  } = useQuery({
    queryKey: ["machine-stats", selectedMachine, timeRange],
    queryFn: async () => {
      if (!selectedMachine) return null;
      const query: Record<string, string> = {};
      if (timeRange !== "all") {
        query.range = timeRange;
        query.tzOffset = String(new Date().getTimezoneOffset());
      }
      const response = await api.stats
        .machines({ identifier: selectedMachine })
        .get({ query });
      if (response.data && "machine" in response.data) {
        return response.data as MachineResponse;
      }
      throw new Error("Failed to fetch machine stats");
    },
    enabled: !!selectedMachine,
    staleTime: 30000, // 30 seconds
  });

  // Sort tiers by displayOrder (lowest first)
  const sortedTiers = useMemo(() => {
    if (!machineData?.tiers) return [];
    return [...machineData.tiers].sort(
      (a, b) => a.displayOrder - b.displayOrder
    );
  }, [machineData?.tiers]);

  // Get default tier (lowest displayOrder)
  const defaultTierId = sortedTiers[0]?.id ?? null;

  // Load saved tier when machine changes
  useEffect(() => {
    if (selectedMachine) {
      const savedTierId = getSavedTierId(selectedMachine);
      setSelectedTierId(savedTierId);
    } else {
      setSelectedTierId(null);
    }
  }, [selectedMachine, getSavedTierId]);

  // Handle tier selection
  const handleTierSelect = useCallback(
    (tierId: number) => {
      setSelectedTierId(tierId);
      if (selectedMachine && machineData?.tiers) {
        saveTierId(selectedMachine, tierId);

        // Find tier name for tracking
        const tier = machineData.tiers.find((t) => t.id === tierId);

        // Track tier view on gap page
        trackEvent("gap-tier-view", {
          machine: selectedMachine,
          timeRange: timeRange,
          tierId: String(tierId),
          tierName: tier?.name ?? "Unknown",
        });
      }
    },
    [selectedMachine, saveTierId, timeRange, machineData?.tiers]
  );

  // Use saved tier, or fall back to default (lowest displayOrder)
  const effectiveTierId = selectedTierId ?? defaultTierId;

  // Fetch gap data for selected tier (single mode)
  const { data: gapData, isLoading: isLoadingGaps } = useQuery({
    queryKey: ["machine-gaps", selectedMachine, effectiveTierId, timeRange],
    queryFn: async () => {
      if (!selectedMachine || !effectiveTierId) return null;
      const query: Record<string, string> = {
        tierId: String(effectiveTierId),
        limit: "30",
      };
      if (timeRange !== "all") {
        query.range = timeRange;
        query.tzOffset = String(new Date().getTimezoneOffset());
      }
      const response = await api.stats
        .machines({ identifier: selectedMachine })
        .gaps.get({ query });
      if (response.data && "gaps" in response.data) {
        return response.data as GapResponse;
      }
      throw new Error("Failed to fetch gap data");
    },
    enabled: !!selectedMachine && !!effectiveTierId && viewMode === "single",
    staleTime: 30000,
  });

  // Fetch all tiers gap data (all mode)
  const { data: allTiersData, isLoading: isLoadingAllTiers } = useQuery({
    queryKey: ["machine-gaps-all-tiers", selectedMachine, timeRange],
    queryFn: async () => {
      if (!selectedMachine) return null;
      const query: Record<string, string> = {
        limitPerTier: "30",
      };
      if (timeRange !== "all") {
        query.range = timeRange;
        query.tzOffset = String(new Date().getTimezoneOffset());
      }
      const response = await api.stats
        .machines({ identifier: selectedMachine })
        ["gaps"]["all-tiers"].get({ query });
      if (response.data && "gaps" in response.data) {
        return response.data as AllTiersGapResponse;
      }
      throw new Error("Failed to fetch all tiers gap data");
    },
    enabled: !!selectedMachine && viewMode === "all",
    staleTime: 30000,
  });

  // Get current tier info
  const currentTier = machineData?.tiers.find((t) => t.id === effectiveTierId);

  // No machine selected state
  if (!selectedMachine) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Card className="max-w-md">
          <CardContent className="py-8 text-center">
            <Info className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-lg font-semibold mb-2">No Machine Selected</h2>
            <p className="text-muted-foreground">
              Select a machine from the dropdown in the header to view stats.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (machineError) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Card className="max-w-md border-destructive/50">
          <CardContent className="py-8 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto text-destructive mb-4" />
            <h2 className="text-lg font-semibold mb-2">Failed to Load Data</h2>
            <p className="text-muted-foreground">
              {machineError instanceof Error
                ? machineError.message
                : "An unexpected error occurred"}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isLoading =
    isLoadingMachine ||
    (viewMode === "single" ? isLoadingGaps : isLoadingAllTiers);

  // Build stats for StatsGrid
  // Note: If the selected tier has no stats (no wins yet), show 0 - don't fall back to totalTierWins
  // currentGap is always cumulative (all-time spins since last tier win), regardless of time range filter
  // Backend now always returns stats.currentGap with correct all-time value
  // For multi-tier view, use time-filtered wins from machineData.tiers (not allTiersData which has all-time cached values)
  const timeFilteredAllTierWins =
    machineData?.tiers.reduce((sum, t) => sum + (t.stats?.totalWins ?? 0), 0) ??
    0;
  const stats: MachineStats =
    viewMode === "single"
      ? {
          totalSpins: machineData?.stats.totalSpins ?? 0,
          tierWins: currentTier?.stats?.totalWins ?? 0,
          tierName: currentTier?.name ?? "Tier Wins",
          tierColor: currentTier?.color,
          currentGap:
            currentTier?.stats?.currentGap ?? gapData?.currentGap ?? 0,
          activeUsers: machineData?.activeUsers ?? 0,
          isMultiTierView: false,
        }
      : {
          totalSpins: machineData?.stats.totalSpins ?? 0,
          tierWins: timeFilteredAllTierWins,
          tierName: "All Tiers",
          tierColor: undefined,
          currentGap: 0, // Not used in multi-tier view
          activeUsers: machineData?.activeUsers ?? 0,
          isMultiTierView: true,
        };

  // Build gap data for chart
  const gaps: GapData[] =
    viewMode === "single"
      ? gapData?.gaps.map((g) => ({
          id: g.id,
          gap: g.gap,
          timestamp: g.timestamp,
        })) ?? []
      : allTiersData?.gaps.map((g) => ({
          id: g.id,
          gap: g.gap,
          timestamp: g.timestamp,
          tierId: g.tierId,
          tierName: g.tierName,
          tierColor: g.tierColor,
        })) ?? [];

  // Build statistical data
  const actualProbability =
    stats.totalSpins > 0 ? stats.tierWins / stats.totalSpins : 0;
  const statisticalData: StatisticalData = {
    minGap: gapData?.minGap ?? currentTier?.stats?.minGap ?? 0,
    maxGap: gapData?.maxGap ?? currentTier?.stats?.maxGap ?? 0,
    averageGap: gapData?.averageGap ?? currentTier?.stats?.averageGap ?? 0,
    medianGap: currentTier?.stats?.averageGap ?? 0, // API doesn't provide median, use average
    tierWins: currentTier?.stats?.totalWins ?? 0,
    tierName: currentTier?.name ?? "Tier",
    tierColor: currentTier?.color,
    totalSpins: machineData?.stats.totalSpins ?? 0,
    actualProbability,
    configuredProbability: currentTier?.probability ?? null,
  };

  // Check if tiers are configured
  const tiersConfigured = machineData?.machine.tiersConfigured ?? false;
  const hasTiers = (machineData?.tiers.length ?? 0) > 0;

  return (
    <div className="space-y-6">
      {/* Warning banner if tiers not configured */}
      {!tiersConfigured && !isLoading && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-amber-400 font-medium">Tiers Not Configured</p>
            <p className="text-sm text-amber-400/80 mt-1">
              Prize tiers haven't been configured for this machine yet. Stats
              will be limited until an admin sets up tier classifications.
            </p>
          </div>
        </div>
      )}

      {/* View Mode Toggle and Tier Selector */}
      {hasTiers && sortedTiers.length > 1 && (
        <div className="space-y-4">
          {/* View Mode Toggle */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground mr-2">
              View Mode:
            </span>
            <button
              onClick={() => setViewMode("single")}
              className={cn(
                "px-3 py-1.5 rounded-full text-xs font-medium transition-colors border flex items-center gap-1.5",
                viewMode === "single"
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
              )}>
              <LayoutList className="w-3 h-3" />
              Single Tier
            </button>
            <button
              onClick={() => setViewMode("all")}
              className={cn(
                "px-3 py-1.5 rounded-full text-xs font-medium transition-colors border flex items-center gap-1.5",
                viewMode === "all"
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
              )}>
              <LayoutGrid className="w-3 h-3" />
              All Tiers
            </button>
          </div>

          {/* Tier Selector (only in single mode) */}
          {viewMode === "single" && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-muted-foreground mr-2">
                View Tier:
              </span>
              {sortedTiers.map((tier) => (
                <button
                  key={tier.id}
                  onClick={() => handleTierSelect(tier.id)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                    effectiveTierId === tier.id
                      ? "border-transparent text-white"
                      : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
                  }`}
                  style={
                    effectiveTierId === tier.id
                      ? { backgroundColor: tier.color }
                      : undefined
                  }>
                  {tier.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stats Cards */}
      <StatsGrid stats={stats} isLoading={isLoading} />

      {/* Streak Analysis Chart */}
      {viewMode === "single" ? (
        <>
          <StreakAnalysisChart
            gaps={gaps}
            currentGap={stats.currentGap}
            averageGap={statisticalData.averageGap}
            tierName={currentTier?.name ?? "Tier Wins"}
            tierColor={currentTier?.color}
            maxDisplayed={25}
            isLoading={isLoading}
            overallMinGap={gapData?.minGap}
            overallMaxGap={gapData?.maxGap}
          />
          {/* Statistical Insight */}
          <StatisticalInsight data={statisticalData} isLoading={isLoading} />
        </>
      ) : (
        /* Multi-tier view: Show individual tier charts stacked */
        <div className="space-y-3">
          {allTiersData?.tiers
            .sort((a, b) => {
              // Highest-value tier first (Epic on top, Common at bottom).
              const tierA = machineData?.tiers.find((t) => t.id === a.tierId);
              const tierB = machineData?.tiers.find((t) => t.id === b.tierId);
              return (tierB?.displayOrder ?? 0) - (tierA?.displayOrder ?? 0);
            })
            .map((tierData) => {
              // Filter gaps for this specific tier
              const tierGaps = allTiersData.gaps
                .filter((g) => g.tierId === tierData.tierId)
                .map((g) => ({
                  id: g.id,
                  gap: g.gap,
                  timestamp: g.timestamp,
                }));

              // Get time-filtered stats from machineData.tiers (not allTiersData which uses cached all-time values)
              const tierStats = machineData?.tiers.find(
                (t) => t.id === tierData.tierId
              )?.stats;
              const timeFilteredWins =
                tierStats?.totalWins ?? tierData.totalWins;

              // Calculate observed probability using time-filtered values
              const totalSpins = machineData?.stats.totalSpins ?? 0;
              const observedProb =
                totalSpins > 0 ? timeFilteredWins / totalSpins : undefined;

              return (
                <StreakAnalysisChart
                  key={tierData.tierId}
                  gaps={tierGaps}
                  currentGap={tierData.currentGap}
                  averageGap={tierData.averageGap}
                  tierName={tierData.tierName ?? `Tier ${tierData.tierId}`}
                  tierColor={tierData.tierColor ?? undefined}
                  maxDisplayed={25}
                  isLoading={isLoading}
                  compact={true}
                  totalWins={timeFilteredWins}
                  observedProbability={observedProb}
                  overallMinGap={tierData.minGap}
                  overallMaxGap={tierData.maxGap}
                />
              );
            })}
        </div>
      )}
    </div>
  );
}
